import { labelsFor } from '@/hooks/nami/filterLabels';
import type {
  DateRangeValue,
  UrlFilterFieldSpec,
  UrlFilterOption,
} from '@/hooks/nami/useUrlFilterTools';
import {
  resolvePreset,
  type PresetKey,
} from '@/components/common/date-pickers/dateRangePresets';

/** Named periods the AP date filters accept instead of dates. */
const AP_DATE_PRESETS: readonly PresetKey[] = [
  'thisWeek',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'all',
];

function resolveApPreset(preset: string): DateRangeValue | undefined {
  const key = AP_DATE_PRESETS.find((p) => p === preset);
  return key ? resolvePreset(key) : undefined;
}

/** A from/to date filter over two YYYY-MM-DD keys of the page's URL search. */
export function apDateRangeField<
  S extends { page?: number } & Partial<Record<F | T, string>>,
  F extends string,
  T extends string,
>(spec: {
  toolName: string;
  label: string;
  description: string;
  from: F;
  to: T;
}): UrlFilterFieldSpec<S> {
  const { from, to } = spec;
  return {
    toolName: spec.toolName,
    description: spec.description,
    label: spec.label,
    kind: 'dateRange',
    presetNames: AP_DATE_PRESETS,
    resolvePreset: resolveApPreset,
    apply: (range, prev) => ({
      ...prev,
      page: undefined,
      [from]: range?.from,
      [to]: range?.to,
    }),
    describe: (s) =>
      s[from] || s[to] ? `${s[from] ?? '…'} → ${s[to] ?? '…'}` : undefined,
  };
}

/** A single-supplier filter; names resolve through the server's `search_suppliers`. */
export function apSupplierField<
  S extends { page?: number; supplierId?: string },
>(spec: {
  toolName: string;
  description: string;
  options: UrlFilterOption[];
}): UrlFilterFieldSpec<S> {
  return {
    toolName: spec.toolName,
    description: spec.description,
    label: 'Supplier',
    kind: 'ids',
    options: () => spec.options,
    resolveHint:
      'Pass a supplier id. Resolve a name with search_suppliers first, then call this tool with the id.',
    apply: (values, prev) => ({
      ...prev,
      page: undefined,
      supplierId: values?.[0],
    }),
    describe: (s) =>
      s.supplierId ? labelsFor([s.supplierId], spec.options) : undefined,
  };
}

/** A multi-supplier filter; names resolve through the server's `search_suppliers`. */
export function apSupplierIdsField<
  S extends { page?: number; supplierIds?: string[] },
>(spec: {
  toolName: string;
  description: string;
  options: UrlFilterOption[];
  max: number;
}): UrlFilterFieldSpec<S> {
  return {
    toolName: spec.toolName,
    description: spec.description,
    label: 'Supplier',
    kind: 'ids',
    options: () => spec.options,
    resolveHint:
      'Pass supplier ids. Resolve a name with search_suppliers first, then call this tool with the ids.',
    apply: (values, prev) => ({
      ...prev,
      page: undefined,
      supplierIds: values?.length ? values.slice(0, spec.max) : undefined,
    }),
    describe: (s) => labelsFor(s.supplierIds, spec.options),
  };
}
